//================================================================
//  Component: Delete Subnet Modal
//================================================================

//  Purpose: Flag the current subnet for deletion (backend service deletes and any children)

//  Properties:
//    - subnetDoc = {useState, contains the application document}
//    - setSubnetDoc = {useState used to determine if the side pane is open/closed}

//  DeleteSubnetModal:
//    <DeleteSubnetModal
//      subnetDoc={agreement}
//      setSubnetDoc={setAgreementPane}
//    ></DeleteSubnetModal>    

//================================================================

//Libraries
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../../../../Library/GlobalContexts';

//Components

//Functions
import QueryDocument from '../../../../../../Library/QueryDocument';
import WriteDocument from '../../../../../../Library/WriteDocument';

//Images
import errorIcon from '../../../../../../Components/Images/Icon_Error_Red.svg';
import LoadingIcon from '../../../../../../Components/Images/Image_Loading_Ripple.svg';
import Error from '../../../../../../Components/Images/Icon_Warning_Red.svg';


export default function DeleteSubnetModal({
    subnetDoc,
    setSubnetDoc,
}) {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const navigate = useNavigate();

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the status of the pane > 'pending', 'onload', 'error'
    const [modalStatus, setModalStatus] = useState('pending');

    // Holds a list of child subnets, populated onload
    const [childSubnets, setChildSubnets] = useState(0);

    // Confirm the subnet should be deleted, by typing in REMOVE
    const [confirmDelete, setConfirmDelete] = useState('');

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submits changes to the table
    const deleteSubnet = () => {

        const document = {
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
            'status': 'inactive'
        };

        WriteDocument('subnets', subnetDoc?.subnetid, document, true).then(() => {

            navigate('/inventory?view=subnets');

        }).catch((error) => {

            console.log('error', error);
            setModalStatus('error');
        });

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Onload
    // - If the current subnet is a 'region'. Get all nested subnets
    useEffect(() => {

        if (subnetDoc === undefined) return;
        if (modalStatus === 'onload') return;

        // Subnets CAN NOT have any children! They are connected to a 'region' supernet
        if (subnetDoc?.type === 'subnet') return setModalStatus('onload');

        QueryDocument('subnets', [
            ['supernetid', '==', subnetDoc?.supernetid]
        ]).then((subnets) => {

            setChildSubnets(subnets.length - 1);
            setModalStatus('onload');

        }).catch((error) => {

            console.log('error', error);
            setModalStatus('error');

        });

        // eslint-disable-next-line
    }, [subnetDoc]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (subnetDoc === undefined) return null;

    //====================================================
    //  Pending
    //====================================================

    if (modalStatus === 'pending') {

        return (
            <div className='Modal-Background'>
                <dialog className='Warning-Modal-Container w-[600px]'>

                    <img className='w-[130px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

                    <button className='Primary-Button w-fit self-center' onClick={() => {

                        setSubnetDoc(undefined);
                        setModalStatus('pending');

                    }}>
                        Cancel
                    </button>

                </dialog >
            </div>
        )

    }

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    if (modalStatus === 'onload') {

        return (
            <div className='Modal-Background'>

                {/* Modal Container */}
                <dialog className='Warning-Modal-Container'>

                    {/* ------------------------------- */}
                    {/*  Header                         */}
                    {/* ------------------------------- */}

                    <h4 className='pb-3 border-b-2 border-solid border-[#D8D8D8]'>You are about to delete subnet '{subnetDoc?.subnetip}'</h4>

                    {/* ------------------------------- */}
                    {/*  Body                           */}
                    {/* ------------------------------- */}

                    <div className='flex flex-col gap-2 pb-4 border-b-2 border-solid border-[#D8D8D8]'>

                        <div className='flex flex-row gap-2 px-3 py-3 mt-2 mb-2 bg-[#FCF4F6] border-1 border-[#C4314B] rounded'>
                            <img src={errorIcon} alt='Warning-Icon-Red'></img>
                            <div className='w-[500px]'>
                                Are you sure you want to delete this subnet, this action <strong>can not be reversed</strong> and will impact <strong>{childSubnets}</strong> nested subnets!
                            </div>
                        </div>

                        {/* Type in REMOVE to confirm delete */}
                        <div className='flex flex-col gap-2'>
                            <label>Type <strong>REMOVE</strong> to confirm</label>
                            <input
                                className='Input-Field-Text'
                                onChange={(e) => setConfirmDelete(e.target.value)}
                            ></input>
                        </div>

                    </div>

                    {/* ------------------------------- */}
                    {/*  Buttons                        */}
                    {/* ------------------------------- */}

                    <div className='flex flex-row gap-2'>

                        <button disabled={!(confirmDelete === 'REMOVE')} className='Warning-Button' onClick={() => deleteSubnet()}>Delete</button>
                        <button
                            className='Secondary-Button'
                            style={{ color: '#424242', border: '1px solid #424242' }}
                            onClick={() => {

                                setSubnetDoc(undefined);
                                setModalStatus('pending');

                            }}
                        >
                            Cancel
                        </button>

                    </div>

                </dialog>

            </div>
        )

    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else {

        return (
            <div className='Modal-Background'>

                {/* Modal Container */}
                <dialog className='Warning-Modal-Container justify-center items-center w-[600px]'>

                    <img className='my-1 w-[50px]' src={Error} alt='Error'></img>

                    <h4 className='mb-0'>Oops! Something went wrong.</h4>

                    <p className='text-center leading-[1.7]'>
                        An error occurred while we processed your request.
                    </p>

                    <button
                        className='Warning-Button'
                        onClick={() => {
                            setModalStatus('onload');
                            setSubnetDoc(undefined);
                        }}
                    >
                        Close
                    </button>

                </dialog>

            </div>
        )

    }

    //------------------------------------------------------

}
